import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  Video,
  Calendar,
  Clock,
  Play,
  ExternalLink,
  Shield,
  Key,
  AlertTriangle
} from 'lucide-react';

const WebinarAccessPage = () => {
  const { webinarId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [accessGranted, setAccessGranted] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [webinarData, setWebinarData] = useState(null);
  const [manualToken, setManualToken] = useState('');

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      validateToken(tokenFromUrl);
    } else {
      setLoading(false);
    }
  }, [searchParams, webinarId]);

  const validateToken = async (token) => {
    setValidating(true);
    setErrorMessage('');
    
    try {
      const response = await api.post(`/webinars/${webinarId}/validate-access?access_token=${token}`);
      
      if (response.data.valid) {
        setAccessGranted(true);
        setWebinarData(response.data);
        toast({
          title: "Acceso validado",
          description: "Tu token ha sido verificado correctamente"
        });
      }
    } catch (error) {
      console.error('Token validation error:', error);
      setAccessDenied(true);
      setErrorMessage(error.response?.data?.detail || 'Token de acceso inválido');
      toast({
        title: "Acceso denegado",
        description: error.response?.data?.detail || "Token de acceso inválido",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setValidating(false);
    }
  };

  const handleManualValidation = (e) => {
    e.preventDefault();
    if (manualToken.trim()) {
      validateToken(manualToken.trim());
    }
  };

  const openWebinar = () => {
    if (webinarData?.meeting_url) {
      window.open(webinarData.meeting_url, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Verificando tu acceso...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Access granted - show webinar access
  if (accessGranted && webinarData) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-50 py-12 px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="shadow-xl border-0 overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-500 p-8 text-center text-white">
                <div className="h-20 w-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <h1 className="text-3xl font-bold mb-2" data-testid="access-granted-title">¡Acceso Verificado!</h1>
                <p className="text-green-100">Ya puedes entrar al webinar</p>
              </div>
              
              <CardContent className="p-8">
                {/* Webinar Info */}
                <div className="bg-gray-50 rounded-xl p-6 mb-6">
                  <h2 className="font-bold text-xl text-gray-900 mb-3" data-testid="webinar-title">
                    {webinarData.title}
                  </h2>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Video className="h-4 w-4 mr-2" />
                      Plataforma: {webinarData.platform}
                    </div>
                    {webinarData.meeting_id && (
                      <div className="flex items-center">
                        <Key className="h-4 w-4 mr-2" />
                        ID de reunión: {webinarData.meeting_id}
                      </div>
                    )}
                    {webinarData.password && (
                      <div className="flex items-center">
                        <Shield className="h-4 w-4 mr-2" />
                        Contraseña: <code className="ml-2 bg-gray-200 px-2 py-0.5 rounded">{webinarData.password}</code>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Button */}
                <Button 
                  size="lg"
                  className="w-full bg-gray-900 hover:bg-gray-800 h-14 text-lg"
                  onClick={openWebinar}
                  data-testid="enter-webinar-btn"
                >
                  <Play className="h-5 w-5 mr-2" />
                  Entrar al Webinar
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>

                {/* Important Notice */}
                <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-800">
                      <p className="font-semibold mb-1">Recuerda:</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Este acceso es personal e intransferible</li>
                        <li>Solo puedes usar este enlace una vez</li>
                        <li>Si compartes tu enlace, podrías perder el acceso</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  // Token validation form (no token in URL or invalid)
  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-50 py-12 px-4">
        <div className="max-w-md mx-auto">
          <Card className="shadow-xl border-0 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-8 text-center text-white">
              <div className="h-16 w-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                {accessDenied ? (
                  <XCircle className="h-8 w-8 text-red-400" />
                ) : (
                  <Key className="h-8 w-8" />
                )}
              </div>
              <h1 className="text-2xl font-bold mb-2" data-testid="access-page-title">
                {accessDenied ? 'Acceso Denegado' : 'Verificar Acceso'}
              </h1>
              <p className="text-gray-300">
                {accessDenied 
                  ? errorMessage 
                  : 'Ingresa tu token de acceso único'}
              </p>
            </div>
            
            <CardContent className="p-8">
              {accessDenied && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800">
                  <p className="font-semibold mb-2">Posibles razones:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Token incorrecto o expirado</li>
                    <li>El pago no ha sido completado</li>
                    <li>El token ya fue utilizado</li>
                  </ul>
                </div>
              )}

              <form onSubmit={handleManualValidation} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Token de Acceso
                  </label>
                  <Input
                    type="text"
                    placeholder="Pega tu token aquí..."
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                    className="h-12 font-mono text-sm"
                    data-testid="token-input"
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Recibiste este token al completar tu compra
                  </p>
                </div>

                <Button 
                  type="submit"
                  className="w-full bg-gray-900 hover:bg-gray-800 h-12"
                  disabled={validating || !manualToken.trim()}
                  data-testid="validate-token-btn"
                >
                  {validating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Verificar Acceso
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-600 text-center mb-4">
                  ¿No tienes un token?
                </p>
                <div className="flex flex-col gap-2">
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => navigate('/webinars')}
                  >
                    Ver webinars disponibles
                  </Button>
                  <Button 
                    variant="ghost"
                    className="w-full text-gray-500"
                    onClick={() => navigate('/contacto')}
                  >
                    Contactar soporte
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default WebinarAccessPage;
